import React from "react";
import { useTranslation } from "react-i18next";
import { S3_BASE_URL } from '../../utils/Constants';

/**
 * Enhanced DataTable component with pagination support
 * @template T
 * @param {Object} props
 * @param {T[]} props.data - Array of data items
 * @param {{ key: string, header: string, render: (row: T, idx: number) => React.ReactNode, isCheckbox?: boolean, isIndex?: boolean, isImage?: boolean, width?: 'xs'|'sm'|'md'|'lg'|'xl'|'auto'|'full' }[]} props.columns - Column definitions
 * @param {boolean} [props.allSelected] - Whether all rows are selected
 * @param {() => void} [props.onToggleAll] - Handler for select all toggle
 * @param {(id: string|number) => void} [props.onToggleRow] - Handler for row selection toggle
 * @param {Array<string|number>} [props.selectedRowKeys] - Array of selected row keys
 * @param {(row: T) => string|number} props.rowKey - Function to get unique key for each row
 * @param {boolean} [props.isLoading] - Loading state
 * @param {React.ReactNode} [props.emptyState] - Custom empty state component
 * @param {string} [props.error] - Error message to display
 * @param {Object} [props.pagination] - Pagination configuration
 * @param {Object} [props.pagination.paginationInfo] - Pagination info from API
 * @param {number} [props.pagination.currentPage] - Current page number
 * @param {number} [props.pagination.pageSize] - Current page size
 * @param {Function} [props.pagination.onPageChange] - Page change handler
 * @param {Function} [props.pagination.onPageSizeChange] - Page size change handler
 * @param {boolean} [props.pagination.isLoading] - Pagination loading state
 * @param {boolean} [props.pagination.hasError] - Pagination error state
 * @param {string} [props.containerClassName] - Additional CSS classes for container
 */
export function DataTable({
  data,
  columns,
  allSelected = false,
  onToggleAll,
  onToggleRow,
  selectedRowKeys = [],
  rowKey,
  isLoading = false,
  emptyState = null,
  error = null,
  pagination = null,
  containerClassName = "",
}) {
  const { t } = useTranslation();

  // Helper function to get width classes based on column width specification
  const getColumnWidthClasses = (column) => {
    if (column.isCheckbox) return "w-12 min-w-12";
    if (column.isIndex) return "w-16 min-w-16";
    if (column.isImage) return "w-20 min-w-20";
    
    switch (column.width) {
      case 'xs': return "w-16 min-w-16 max-w-16";
      case 'sm': return "w-24 min-w-24 max-w-24";
      case 'md': return "w-32 min-w-32 max-w-32";
      case 'lg': return "w-48 min-w-48 max-w-48";
      case 'xl': return "w-64 min-w-64 max-w-64";
      case 'auto': return "w-auto";
      case 'full': return "w-full";
      default: return "min-w-0"; // Default flexible width
    }
  };

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 shadow-sm">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-red-500 mr-3 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-red-700 font-medium">{error}</span>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={`w-full bg-white rounded-lg shadow-sm border border-gray-200 ${containerClassName}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => {
                  const widthClasses = getColumnWidthClasses(column);
                  if (column.isCheckbox && onToggleAll) {
                    return (
                      <th key={column.key} className={`px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${widthClasses}`}>
                        <label className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={allSelected}
                            onChange={onToggleAll}
                          />
                        </label>
                      </th>
                    );
                  }
                  return (
                    <th key={column.key} className={`px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${widthClasses}`}>
                      <div className="truncate">{column.header}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[...Array(5)].map((_, index) => (
                <tr key={index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  {columns.map((column) => (
                    <td key={column.key} className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-full max-w-32 mx-auto"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={`w-full bg-white rounded-lg shadow-sm border border-gray-200 ${containerClassName}`}>
        {emptyState || (
          <div className="text-center py-12 px-4">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t ? t("common.noDataFound") : "No data found"}
            </h3>
            <p className="text-gray-500">
              {t ? t("common.noDataMessage") : "There is no data to display"}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full bg-white rounded-lg shadow-sm border border-gray-200 ${containerClassName}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => {
                const widthClasses = getColumnWidthClasses(column);
                if (column.isCheckbox && onToggleAll) {
                  return (
                    <th key={column.key} className={`px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${widthClasses}`}>
                      <label className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={allSelected}
                          onChange={onToggleAll}
                        />
                      </label>
                    </th>
                  );
                }
                return (
                  <th key={column.key} className={`px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${widthClasses}`}>
                    <div className="truncate">{column.header}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => {
              const key = rowKey ? rowKey(row) : index;
              const isSelected = selectedRowKeys.includes(key);
              
              return (
                <tr key={key} className={`transition-colors duration-150 ${index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100/70'}`}>
                  {columns.map((column) => {
                    const widthClasses = getColumnWidthClasses(column);
                    
                    if (column.isCheckbox && onToggleRow) {
                      return (
                        <td key={column.key} className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center ${widthClasses}`}>
                          <label className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={isSelected}
                              onChange={() => onToggleRow(key)}
                            />
                          </label>
                        </td>
                      );
                    }
                    
                    if (column.isIndex) {
                      return (
                        <td key={column.key} className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center ${widthClasses}`}>
                          {pagination && pagination.currentPage !== undefined 
                            ? pagination.currentPage * pagination.pageSize + index + 1
                            : index + 1
                          }
                        </td>
                      );
                    }
                    
                    if (column.isImage) {
                      const imageSrc = column.render ? column.render(row, index) : row[column.key];
                      // Handle case where imageSrc might be an array or invalid value
                      const imageUrl = Array.isArray(imageSrc) ? imageSrc[0] : imageSrc;
                      const validImageUrl = typeof imageUrl === 'string' ? imageUrl : '';
                      
                      return (
                        <td key={column.key} className={`px-3 sm:px-6 py-4 whitespace-nowrap text-center ${widthClasses}`}>
                          <div className="flex items-center justify-center">
                            <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                              {validImageUrl ? (
                                <img
                                  className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover"
                                  src={validImageUrl?.startsWith?.('http') ? validImageUrl : `${S3_BASE_URL}${validImageUrl}`}
                                  alt="Item"
                                  onError={(e) => {
                                    e.target.src = '/placeholder-image.png';
                                  }}
                                />
                              ) : (
                                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">No img</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    }
                    
                    return (
                      <td key={column.key} className={`px-3 sm:px-6 py-4 text-sm text-gray-900 text-center ${widthClasses}`}>
                        <div className="truncate">
                          {column.render ? column.render(row, index) : row[column.key]}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && <PaginationControls pagination={pagination} t={t} />}
    </div>
  );
}

/**
 * Pagination Controls Component
 * @param {Object} props
 * @param {Object} props.pagination - Pagination configuration
 * @param {Function} props.t - Translation function
 */
function PaginationControls({ pagination, t }) {
  const {
    paginationInfo,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    isLoading,
    hasError
  } = pagination;

  // Don't show pagination if loading, has error, or no pagination info
  if (isLoading || hasError || !paginationInfo || paginationInfo.totalPages <= 1) {
    return null;
  }

  const { totalPages, totalElements } = paginationInfo;

  return (
    <>
      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-center items-center mt-6 space-y-2 sm:space-y-0 sm:space-x-1 px-4">
        <div className="flex items-center space-x-1 order-2 sm:order-1">
          <button
            className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === 0}
            onClick={() => onPageChange(0)}
          >
            <span className="hidden sm:inline">« {t && t("customers.first") ? t("customers.first") : "First"}</span>
            <span className="sm:hidden">««</span>
          </button>
          <button
            className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border-t border-b border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === 0}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <span className="hidden sm:inline">‹ {t && t("customers.previous") ? t("customers.previous") : "Previous"}</span>
            <span className="sm:hidden">‹</span>
          </button>

          {/* Page Numbers */}
          {Array.from(
            { length: Math.min(3, totalPages) }, // Show fewer pages on mobile
            (_, i) => {
              let pageNum = currentPage - 1 + i;
              if (pageNum < 0) pageNum = i;
              if (pageNum >= totalPages)
                pageNum = totalPages - 3 + i;
              if (pageNum < 0 || pageNum >= totalPages)
                return null;

              return (
                <button
                  key={pageNum}
                  className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border-t border-b border-gray-300 hover:bg-gray-50 ${
                    currentPage === pageNum 
                      ? "bg-blue-50 text-blue-600 border-blue-500" 
                      : "bg-white text-gray-500"
                  }`}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            }
          )}

          <button
            className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border-t border-b border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage >= totalPages - 1}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <span className="hidden sm:inline">{t && t("customers.next") ? t("customers.next") : "Next"} ›</span>
            <span className="sm:hidden">›</span>
          </button>
          <button
            className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage >= totalPages - 1}
            onClick={() => onPageChange(totalPages - 1)}
          >
            <span className="hidden sm:inline">{t && t("customers.last") ? t("customers.last") : "Last"} »</span>
            <span className="sm:hidden">»»</span>
          </button>
        </div>
      </div>

      {/* Pagination Info */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-3 sm:px-6 py-3 bg-gray-50 border-t border-gray-200 space-y-2 sm:space-y-0">
        <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
          <span className="hidden sm:inline">
            {t && t("customers.showing") ? t("customers.showing") : "Showing"} {currentPage * pageSize + 1}{" "}
            {t && t("customers.to") ? t("customers.to") : "to"}{" "}
            {Math.min((currentPage + 1) * pageSize, totalElements)}{" "}
            {t && t("customers.of") ? t("customers.of") : "of"} {totalElements}{" "}
            {t && t("customers.results") ? t("customers.results") : "results"}
          </span>
          <span className="sm:hidden">
            {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}
          </span>
        </div>
        <div className="flex items-center space-x-2 order-1 sm:order-2">
          <span className="text-xs sm:text-sm text-gray-700">{t && t("customers.items") ? t("customers.items") : "Items"}:</span>
          <select
            className="border border-gray-300 rounded-md px-2 py-1 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </>
  );
}

export default DataTable;